import { useState } from 'react';
import { useTranslation } from '@doccareplus/i18n';
import { Avatar, EmptyState, LoadingSpinner, SearchInput } from '@doccareplus/ui';
import { COUNTRIES, detectDefaultCountry, type Country } from '@doccareplus/shared/lib/phone';
import { selectActiveContext, useAppSelector } from '@doccareplus/kernel';
import { usePatientSearchQuery } from '../application/scheduling.query';
import type { PatientPickOption } from '../infrastructure/scheduling.api';

/** Returns true when the string looks like a phone number (starts with + or is mostly digits). */
function looksLikePhone(term: string): boolean {
  const cleaned = term.replace(/[\s\-()]/g, '');
  return /^\+?\d{5,}$/.test(cleaned);
}

/** Bare digits of a phone, for detecting the same number shared across family records. */
function digitsOf(phone?: string | null): string {
  return (phone ?? '').replace(/\D/g, '');
}

function countryForInternationalDigits(digits: string): Country | undefined {
  let match: Country | undefined;
  for (const country of COUNTRIES) {
    if (digits.startsWith(country.dialCode) && (!match || country.dialCode.length > match.dialCode.length)) {
      match = country;
    }
  }
  return match;
}

function ChevronIcon() {
  return (
    <svg className="h-4 w-4 flex-none text-text-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export interface QuickRegisterPrefillFromSearch {
  name: string;
  phone: string;
  phoneCountry: string;
}

/**
 * Patient Search & Link — the design's custom patient picker (not a generic select). An always-visible
 * search box drives a results dropdown of avatar rows (name · patient number · phone), with a
 * "Register new patient" action pinned at the bottom that is ALWAYS available — even when results exist —
 * so a new family member sharing a phone can be created without picking the wrong record. Registering a
 * new patient is a separate step owned by the parent (a 2-step stepper: register → booking details, see
 * `BookAppointmentDrawer`/`BookAppointmentSheet`) — this component only reports the intent + a prefill
 * guess derived from whatever was typed in the search box via `onRegisterNew`. Selecting an existing
 * result reports the chosen `PatientPickOption` up via `onSelect`; once linked, the box collapses to a
 * confirmation chip with a "Change" action.
 */
export function PatientSearchAndLink({
  selected,
  onSelect,
  onRegisterNew,
}: {
  selected: PatientPickOption | null;
  onSelect: (patient: PatientPickOption | null) => void;
  onRegisterNew: (prefill: QuickRegisterPrefillFromSearch) => void;
}) {
  const { t } = useTranslation('appointments');
  const clinicCountry = useAppSelector(selectActiveContext)?.country;

  const [term, setTerm] = useState('');
  const { data: results, isFetching } = usePatientSearchQuery(term);

  // ── Selected patient chip ───────────────────────────────────────────
  // Design's linked-patient card: primary-tinted panel, avatar, name + "Linked"
  // confirmation, and the patient number · phone line for a last-glance check.
  if (selected) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-primary bg-primary-weak px-3.5 py-3">
        <Avatar name={selected.fullName} size="lg" brand />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-14 font-bold text-text">{selected.fullName}</span>
            <span className="inline-flex items-center gap-1 text-11 font-bold text-success">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
              {t('booking.patientLinked', { defaultValue: 'Linked' })}
            </span>
          </div>
          <div className="mt-0.5 truncate font-mono text-12 text-text-2">
            {[selected.patientNumber, selected.phone].filter(Boolean).join(' · ')}
          </div>
        </div>
        <button
          type="button"
          className="flex-none rounded-lg bg-surface px-3 py-1.5 text-12 font-semibold text-primary hover:bg-surface-2"
          onClick={() => {
            onSelect(null);
            setTerm('');
          }}
        >
          {t('common:actions.change', { defaultValue: 'Change' })}
        </button>
      </div>
    );
  }

  const trimmed = term.trim();
  const showDropdown = trimmed.length >= 2;
  const rows = results ?? [];
  const phoneCounts = rows.reduce<Record<string, number>>((acc, p) => {
    const key = digitsOf(p.phone);
    if (key) acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  const requestRegister = () => {
    // Pre-fill from search term: phone-looking → phone, text-looking → name.
    const defaultCountry = clinicCountry || detectDefaultCountry();
    if (looksLikePhone(trimmed)) {
      const digits = trimmed.replace(/\D/g, '');
      const country = trimmed.startsWith('+') ? countryForInternationalDigits(digits) : undefined;
      onRegisterNew({
        name: '',
        phone: country ? digits.slice(country.dialCode.length) : trimmed,
        phoneCountry: country?.iso2 ?? defaultCountry,
      });
    } else {
      onRegisterNew({ name: trimmed, phone: '', phoneCountry: defaultCountry });
    }
  };

  const registerAction = (
    <button
      type="button"
      onClick={requestRegister}
      className="flex w-full items-center gap-2.5 border-t border-border bg-primary-weak px-3.5 py-3 text-left text-primary transition-colors hover:bg-primary-weak/70"
    >
      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-lg bg-primary text-primary-fg">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </span>
      <span className="flex-1 text-13 font-bold">
        {t('booking.registerNewPatient', { defaultValue: 'Register new patient' })}
      </span>
    </button>
  );

  // ── Search box + results dropdown ───────────────────────────────────
  return (
    <div className="flex flex-col">
      <div className="relative">
        <SearchInput
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onClear={() => setTerm('')}
          clearLabel={t('common:actions.clear')}
          placeholder={t('booking.patientSearchPlaceholder', {
            defaultValue: 'Search by phone, name, or patient number…',
          })}
          aria-label={t('booking.patientSearchPlaceholder', {
            defaultValue: 'Search by phone, name, or patient number…',
          })}
        />
        {isFetching && (
          <LoadingSpinner
            size="sm"
            className="pointer-events-none absolute right-10 top-1/2 -translate-y-1/2"
          />
        )}
      </div>

      {showDropdown && (
        <div className="mt-2 overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
          <div className="max-h-56 overflow-y-auto">
            {isFetching && rows.length === 0 ? (
              [0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3 border-b border-border px-3.5 py-2.5 last:border-b-0">
                  <span className="h-8 w-8 flex-none animate-pulse rounded-full bg-surface-2" />
                  <div className="flex-1">
                    <span className="block h-2.5 w-2/5 animate-pulse rounded bg-surface-2" />
                    <span className="mt-1.5 block h-2 w-2/3 animate-pulse rounded bg-surface-3" />
                  </div>
                </div>
              ))
            ) : rows.length > 0 ? (
              rows.map((p) => {
                const shared = phoneCounts[digitsOf(p.phone)] > 1;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onSelect(p)}
                    className="flex w-full items-center gap-3 border-b border-border px-3.5 py-2.5 text-left transition-colors last:border-b-0 hover:bg-surface-2"
                  >
                    <Avatar name={p.fullName} size="md" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-13 font-semibold text-text">{p.fullName}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 font-mono text-12 text-text-2">
                        <span>{p.patientNumber}</span>
                        {p.phone && <span className="text-text-3">·</span>}
                        {p.phone && <span>{p.phone}</span>}
                        {shared && (
                          <span className="rounded-full bg-warning-bg px-1.5 py-px font-sans text-[10px] font-bold text-warning">
                            {t('booking.sharedNumber', { defaultValue: 'shared no.' })}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronIcon />
                  </button>
                );
              })
            ) : (
              <EmptyState
                variant="text"
                title={t('booking.noPatients', { defaultValue: 'No patients found' })}
                description={t('booking.noPatientsHint', {
                  defaultValue: 'for “{{term}}” — register them below',
                  term: trimmed,
                })}
                className="border-0 shadow-none"
              />
            )}
          </div>
          {registerAction}
        </div>
      )}
    </div>
  );
}
